/**
 * useDocumentComparison - Orchestrates the full document comparison flow.
 *
 * State machine: idle → uploading → processing → completed | failed
 *
 * 1. User drops a zip file
 * 2. File is uploaded directly via multipart/form-data to the create endpoint
 * 3. Poll comparison status every 2s until completed or failed
 */

import { useCallback, useEffect, useState } from 'react';

import {
  type DocumentComparisonSchema,
  useCreateDocumentComparisonMutation,
  useGetDocumentComparisonQuery,
} from '@/services/api/documentComparisonService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ComparisonPhase =
  | 'idle'
  | 'uploading'
  | 'processing'
  | 'completed'
  | 'failed';

export interface UseDocumentComparisonReturn {
  phase: ComparisonPhase;
  comparison: DocumentComparisonSchema | null;
  fileName: string | null;
  error: string | null;
  handleFileDrop: (file: File) => void;
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useDocumentComparison(
  matterId: string,
): UseDocumentComparisonReturn {
  const [phase, setPhase] = useState<ComparisonPhase>('idle');
  const [fileName, setFileName] = useState<string | null>(null);
  const [comparisonId, setComparisonId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // RTK Query mutations
  const [createComparison] = useCreateDocumentComparisonMutation();

  // Poll comparison status (skip when idle/uploading or no comparison ID)
  const isPolling = phase === 'processing' && comparisonId !== null;
  const { data: comparison } = useGetDocumentComparisonQuery(
    { matterId, comparisonId: comparisonId! },
    {
      skip: !isPolling,
      pollingInterval: isPolling ? 2000 : undefined,
    },
  );

  // React to comparison status changes
  useEffect(() => {
    if (!comparison) return;
    if (comparison.status === 'completed') {
      setPhase('completed');
    } else if (comparison.status === 'failed') {
      setPhase('failed');
      setError(comparison.error_message ?? 'Comparison failed');
    }
  }, [comparison]);

  const handleFileDrop = useCallback(
    async (file: File) => {
      if (phase !== 'idle') return;

      setPhase('uploading');
      setFileName(file.name);
      setError(null);

      try {
        // Upload the zip directly via multipart/form-data
        const comp = await createComparison({
          matterId,
          file,
        }).unwrap();

        setComparisonId(comp.id);
        setPhase('processing');
      } catch (err) {
        setPhase('failed');
        setError(err instanceof Error ? err.message : 'Upload failed');
      }
    },
    [phase, matterId, createComparison],
  );

  const reset = useCallback(() => {
    setPhase('idle');
    setFileName(null);
    setComparisonId(null);
    setError(null);
  }, []);

  return {
    phase,
    comparison: comparison ?? null,
    fileName,
    error,
    handleFileDrop,
    reset,
  };
}
